import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import { logError } from "../../../utils/utils";
import { AppDispatch, RootState } from "../../../app/store";
import { errorHandler } from "../../../utils/errorHandling";
import { aktiverMesspunkt, betriebsereignis, messgeraet, messstelle, routergeraet } from "../../../utils/apiTypes";
import { getWithAuth, putToS3, putWithAuth, getXLSXWithAuth } from "../../../utils/api";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";
import { firewallantrag } from "./firewallantragTypes";

export const emptyAktiverMesspunkt: aktiverMesspunkt = {
  name: "",
  schaltfeld: "",
  umspannwerk: "",
  messgeraet: 0,
  router: 0,
};

export const emptyGeraet: messgeraet = {
  id: 0,
  seriennummer: "",
  herstellungsjahr: 0,
  firmware: "",
  macadresse: "",
  kommentar: "",
  inbetriebnahmedatum: "",
  wartungsintervall: "",
  zeitsynchronisationsart: "",
  typ: 0,
  ipadresse: "",
  subnetzmaske: "",
  zugeordnet: false,
  jemalsZugeordnet: false,
};

const initialState: firewallantrag = {
  aktiveMesspunkte: [],
  selectedUW: "",
  isDocumentAlreadyAvailable: false,
  newMode: false,
  messpunktLoading: false,
  selectedAktiverMesspunkt: {} as messstelle,
  geraetDaten: emptyGeraet,
  betriebsereignisse: [],
  isDokumentLoading: false,
  hochlandenDialog: false,
};

const firewallantragSlice = createSlice({
  name: "firewallantrag",
  initialState,
  reducers: {
    setIsDocumentAlreadyAvailable: (state, action: PayloadAction<boolean>) => {
      state.isDocumentAlreadyAvailable = action.payload;
    },
    setSelectedUW: (state, action: PayloadAction<string>) => {
      state.selectedUW = action.payload;
    },
    setAktiveMesspunkte: (state, action: PayloadAction<messstelle[]>) => {
      state.aktiveMesspunkte = action.payload;
    },
    setNewMode: (state, action: PayloadAction<boolean>) => {
      state.newMode = action.payload;
    },
    setMesspunktLoading: (state, action: PayloadAction<boolean>) => {
      state.messpunktLoading = action.payload;
    },
    setSelectedAktiverMesspunkt: (state, action: PayloadAction<messstelle>) => {
      state.selectedAktiverMesspunkt = action.payload;
    },
    setGeraetDaten: (state, action: PayloadAction<messgeraet>) => {
      state.geraetDaten = action.payload;
    },
    setBetriebsereignisse: (state, action: PayloadAction<betriebsereignis[]>) => {
      state.betriebsereignisse = action.payload;
    },
    setIsDokumentLoading: (state, action: PayloadAction<boolean>) => {
      state.isDokumentLoading = action.payload;
    },
    setHochlandenDialog: (state, action: PayloadAction<boolean>) => {
      state.hochlandenDialog = action.payload;
    },
  },
});

export const {
  setIsDocumentAlreadyAvailable,
  setAktiveMesspunkte,
  setMesspunktLoading,
  setNewMode,
  setSelectedUW,
  setSelectedAktiverMesspunkt,
  setGeraetDaten,
  setBetriebsereignisse,
  setIsDokumentLoading,
  setHochlandenDialog,
} = firewallantragSlice.actions;

export const selectIsDocumentAlreadyAvailable = (state: RootState): boolean => state.firewallantrag.isDocumentAlreadyAvailable;
export const selectSelectedUW = (state: RootState): string => state.firewallantrag.selectedUW;
export const selectAktiveMesspunkte = (state: RootState): messstelle[] => state.firewallantrag.aktiveMesspunkte;
export const selectNewMode = (state: RootState): boolean => state.firewallantrag.newMode;
export const selectMesspunktLoading = (state: RootState): boolean => state.firewallantrag.messpunktLoading;
export const selectIsDokumentLoading = (state: RootState): boolean => state.firewallantrag.isDokumentLoading;
export const selectHochlandenDialog = (state: RootState): boolean => state.firewallantrag.hochlandenDialog;

export const generateFirewallAntrag =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getXLSXWithAuth("GENERATEFIREWALLANTRAG", {}, uw)
      .then((response) => {
        const returnedFile = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(returnedFile, "Firewallantrag_" + uw.replace(" ", ""));
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const getAktiveMesspunkte =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(setMesspunktLoading(true));
    getWithAuth("MESSSTELLE", {})
      .then((response: messstelle[]) => {
        dispatch(setAktiveMesspunkte(response));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setMesspunktLoading(false)));
  };

export const getFirewallAntrag =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    const openInSameTab = (url: string) => {
      window.open(url, "_self", "noopener,noreferrer");
    };
    dispatch(setIsDokumentLoading(true));
    getWithAuth("GETFIREWALLANTRAG", {}, uw)
      .then((response) => {
        openInSameTab(response);
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const checkIfFirewallAntragFileIsAvailable =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getWithAuth("GETFIREWALLANTRAG", {}, uw)
      .then((response) => {
        if (response !== null) {
          dispatch(setIsDocumentAlreadyAvailable(true));
        } else {
          console.log("No documents available");
        }
      })
      .catch((error: any) => {
        dispatch(setIsDocumentAlreadyAvailable(false));
        logError(error);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const addFirewallDokument =
  (uw: string, datei: File): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch: AppDispatch) => {
    // const data = new FormData();
    // data.append("file", datei);
    dispatch(setIsDokumentLoading(true));
    putWithAuth("UPLOADFIREWALLANTRAG", {}, {}, uw)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        putToS3(response, datei.type, {}, datei)
          .then(() => {
            dispatch(
              openSnackbar({
                message: `Antrag für UW ${uw} wurde erfolgreich hochgeladen.`,
                severity: "success",
                duration: 6000,
              })
            );
            dispatch(setHochlandenDialog(false));
          })
          .catch((error: any) => {
            logError(error);
            errorHandler(error, dispatch);
            dispatch(setIsDokumentLoading(false));
          });
      })
      .catch((error: any) => {
        logError(error);
        dispatch(setIsDokumentLoading(false));
      });
  };

export const sendFirewallDokument =
  (uw: string): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch: AppDispatch) => {
    dispatch(setIsDokumentLoading(true));
    putWithAuth("SENDFIREWALLANTRAG", {}, {}, uw)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        dispatch(
          openSnackbar({
            message: `Antrag für UW ${uw} wurde erfolgreich an SI gesendet.`,
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
        dispatch(setIsDokumentLoading(false));
      });
  };
export default firewallantragSlice.reducer;
